import type { Module } from 'vuex';
import { LOCAL_STORAGE_KEYS, setLocalStorageValue, removeLocalStorageValue, getLocalStorageValue } from '@/constants/localStorage';
import {RootState} from "@/store";

// Интерфейсы для переводов
export interface TranslateGroupItems {
  [code: string]: string;
}

export interface TranslateGroups {
  [groupName: string]: TranslateGroupItems;
}

export interface TranslatePageItems {
  [code: string]: string;
}

// Рекурсивный интерфейс для breadCrumbs (соответствует бэкенду)
export interface BreadCrumb {
  title: string;
  breadCrumb: string;
  url: string;
  parent: BreadCrumb | null;
}

export interface TranslatePage {
  name: string;
  breadCrumbs: BreadCrumb | null;
  items: TranslatePageItems;
}

export interface TranslateEntity {
  one: string;
  many: string;
}

export interface I18nState {
  idLanguage: number | null;
  idLocale: number | null;
  languageSwitcherDoNotShow: boolean;
  // Новые поля для переводов
  translateGroups: TranslateGroups;
  translatePage: TranslatePage | null; // Текущая страница
  translateEntities: { [key: string]: TranslateEntity }; // Entities по ключам
}

const i18nModule: Module<I18nState, RootState> = {
  namespaced: true,

  state: (): I18nState => ({
    idLanguage: null,
    idLocale: null,
    languageSwitcherDoNotShow: false,
    translateGroups: {},
    translatePage: null,
    translateEntities: {},
  }),

  getters: {
    currentLanguageId: (state): number | null => state.idLanguage,
    currentLocaleId: (state): number | null => state.idLocale,
    isLanguageSwitcherDoNotShow: (state): boolean => state.languageSwitcherDoNotShow,
    
    // Удобный геттер для получения перевода по коду (группы или элементы страницы)
    translate: (state) => (code: string, groupName?: string): string => {
      // Сначала проверяем группы
      if (groupName && state.translateGroups[groupName]?.[code]) {
        return state.translateGroups[groupName][code];
      }
      
      // Затем проверяем элементы страницы
      if (state.translatePage?.items && code in state.translatePage.items) {
        return state.translatePage.items[code];
      }
      
      return code; // Возвращаем код, если перевод не найден
    },

    // Геттеры для работы с breadCrumbs
    getBreadCrumbs: (state): BreadCrumb | null => {
      return state.translatePage?.breadCrumbs || null;
    },

    // Получение массива breadCrumbs для отображения (от корня к текущей странице)
    getBreadCrumbsArray: (state): BreadCrumb[] => {
      const breadCrumbs: BreadCrumb[] = [];
      let current: BreadCrumb | null = state.translatePage?.breadCrumbs || null;
      
      // Собираем breadCrumbs в обратном порядке (от корня к текущей)
      while (current) {
        breadCrumbs.unshift(current);
        current = current.parent;
      }
      
      return breadCrumbs;
    },

    // Получение заголовка текущей страницы
    getPageTitle: (state): string => {
      return state.translatePage?.breadCrumbs?.title || '';
    },

    // Получение названия текущей страницы
    getPageName: (state): string => {
      return state.translatePage?.name || '';
    },

    // Получение перевода сущности по ключу и форме (one/many)
    getEntityTranslation: (state) => (entityKey: string, form: 'one' | 'many'): string => {
      return state.translateEntities[entityKey]?.[form] || entityKey;
    },

    // Получение всех entities
    getAllEntities: (state): { [key: string]: { one: string; many: string } } => {
      return state.translateEntities;
    },
  },

  mutations: {
    setLanguage(
      state,
      payload: number | null
    ) {
      if (payload !== null) {
        state.idLanguage = parseInt(payload.toString());
      } else {
        state.idLanguage = null;
      }

      if (state.idLanguage === null) {
        removeLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LANGUAGE);
      } else {
        let storageIdLanguage = getLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LANGUAGE);
        if (storageIdLanguage === null) {
          if (state.idLanguage !== null) {
            setLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LANGUAGE, state.idLanguage.toString());
          }
        } else if (parseInt(storageIdLanguage.toString()) !== state.idLanguage) {
          if (state.idLanguage !== null) {
            setLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LANGUAGE, state.idLanguage.toString());
          } else {
            removeLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LANGUAGE);
          }
        }
      }
    },

    setLocale(
      state,
      payload: number | null
    ) {
      if (payload !== null) {
        state.idLocale = parseInt(payload.toString());
      } else {
        state.idLocale = null;
      }

      if (state.idLocale === null) {
        removeLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LOCALE);
      } else if (getLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LOCALE) !== state.idLocale.toString()) {
        setLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LOCALE, state.idLocale.toString());
      }
    },

    setLanguageSwitcherDoNotShow(
      state,
      payload: boolean
    ) {
      state.languageSwitcherDoNotShow = payload;
      if (payload) {
        setLocalStorageValue(LOCAL_STORAGE_KEYS.LANGUAGE_SWITCHER_DO_NOT_SHOW, 'true');
      } else {
        removeLocalStorageValue(LOCAL_STORAGE_KEYS.LANGUAGE_SWITCHER_DO_NOT_SHOW);
      }
    },

    // Мутации для переводов групп
    setTranslateGroup(
      state,
      payload: { groupName: string; items: TranslateGroupItems }
    ) {
      // Заменяем весь объект для обеспечения реактивности
      state.translateGroups = {
        ...state.translateGroups,
        [payload.groupName]: payload.items
      };
    },

    setPage(
      state,
      payload: { name: string, title: string, breadCrumb: any, items: { [key: string]: any }, entities: { [key: string]: { one: string; many: string } } }
    ) {
      // Функция для преобразования any данных в типизированный BreadCrumb
      const convertToBreadCrumb = (data: any): BreadCrumb | null => {
        if (!data || typeof data !== 'object') {
          return null;
        }
        
        return {
          title: data.title || '',
          breadCrumb: data.breadCrumb || '',
          url: data.url || '',
          parent: data.parent ? convertToBreadCrumb(data.parent) : null,
        };
      };

      // Преобразуем breadCrumbs в типизированный формат
      const typedBreadCrumbs = convertToBreadCrumb(payload.breadCrumb);

      // Разливаем entities в глобальный массив
      if (payload.entities) {
        state.translateEntities = { ...state.translateEntities, ...payload.entities };
      }

      if (state.translatePage !== null) {
        if (state.translatePage.name !== payload.name) {
          state.translatePage = {
            name: payload.name,
            breadCrumbs: typedBreadCrumbs || state.translatePage.breadCrumbs,
            items: { ...payload.items },
          };
        } else {
          // Обновляем только изменившиеся элементы
          for (const itemKey in payload.items) {
            if (state.translatePage.items[itemKey] !== payload.items[itemKey]) {
              state.translatePage.items[itemKey] = payload.items[itemKey];
            }
          }
          // Обновляем breadCrumbs если они изменились
          if (JSON.stringify(state.translatePage.breadCrumbs) !== JSON.stringify(typedBreadCrumbs)) {
            state.translatePage.breadCrumbs = typedBreadCrumbs || state.translatePage.breadCrumbs;
          }
        }
      } else {
        state.translatePage = {
          name: payload.name,
          breadCrumbs: typedBreadCrumbs || null,
          items: { ...payload.items },
        };
      }
    },

    setTranslateEntity(
      state,
      payload: { key: string; one: string; many: string }
    ) {
      state.translateEntities[payload.key] = { one: payload.one, many: payload.many };
    },

    // Мутация для установки всех переводов из session группы
    setGroupTranslations(
      state,
      payload: { groupName: string, groupItems: { [key: string]: any } }
    ) {
      const { groupName, groupItems } = payload;
      // Заменяем весь объект для обеспечения реактивности
      state.translateGroups = {
        ...state.translateGroups,
        [groupName]: groupItems as TranslateGroupItems
      };
    },

    // Очистка всех переводов
    clearTranslations(state) {
      // Заменяем на пустой объект для обеспечения реактивности
      state.translateGroups = {};
      state.translatePage = null;
      state.translateEntities = {};
    },

    // Обновление заголовка страницы
    updatePageTitle(state, payload: { title: string }) {
      if (state.translatePage?.breadCrumbs) {
        state.translatePage.breadCrumbs.title = payload.title;
      }
    },

    // Обновление breadcrumb страницы
    updatePageBreadcrumb(state, payload: { pageName: string; breadCrumb: string }) {
      const { pageName, breadCrumb } = payload;
      
      // Функция для рекурсивного поиска и обновления breadCrumb
      const updateBreadCrumbRecursive = (current: BreadCrumb | null): boolean => {
        if (!current) {
          return false;
        }
        
        // Сравниваем pageName с URL (убираем слеш в начале)
        const breadCrumbPageName = current.url.replace(/^\//, '');
        
        if (breadCrumbPageName === pageName) {
          current.breadCrumb = breadCrumb;
          return true;
        }
        
        return updateBreadCrumbRecursive(current.parent);
      };
      
      updateBreadCrumbRecursive(state.translatePage?.breadCrumbs || null);
    },

    // Обновление entity глобально
    updateEntityGlobal(state, payload: { entityName: string; one: string; many: string }) {
      const { entityName, one, many } = payload;
      
      // Обновляем entity в глобальном массиве
      if (state.translateEntities[entityName]) {
        state.translateEntities[entityName] = { one, many };
      }
    },

    // Обновление page item глобально
    updatePageItemGlobal(state, payload: { pageName: string; pageItemCode: string; text: string }) {
      const { pageName, pageItemCode, text } = payload;
      
      // Проверяем, что текущая страница соответствует обновляемой
      if (state.translatePage?.name === pageName) {
        // Обновляем соответствующий pageItemCode
        if (state.translatePage.items && pageItemCode in state.translatePage.items) {
          state.translatePage.items[pageItemCode] = text;
        }
      }
    },

    // Обновление group item глобально
    updateGroupItemGlobal(state, payload: { groupName: string; groupItemCode: string; text: string }) {
      const { groupName, groupItemCode, text } = payload;
      
      // Проверяем, что группа существует
      if (state.translateGroups[groupName]) {
        // Обновляем соответствующий groupItemCode
        if (groupItemCode in state.translateGroups[groupName]) {
          state.translateGroups[groupName][groupItemCode] = text;
        }
      }
    },
  },

  actions: {
    setLanguage({ commit }, payload) {
      commit('setLanguage', payload);
    },

    setLocale({ commit }, payload) {
      commit('setLocale', payload);
    },

    setLanguageSwitcherDoNotShow({ commit }, payload) {
      commit('setLanguageSwitcherDoNotShow', payload);
    },

    // Действия для переводов групп
    setTranslateGroup({ commit }, payload) {
      commit('setTranslateGroup', payload);
    },

    // Действие для установки страницы
    setPage({ commit }, payload: { name: string, title: string, breadCrumb: any, items: { [key: string]: any }, entities: { [key: string]: { one: string; many: string } } }) {
      commit('setPage', payload);
    },

    setTranslateEntity({ commit }, payload) {
      commit('setTranslateEntity', payload);
    },

    // Действие для установки переводов из session группы
    setGroupTranslations({ commit }, payload: { groupName: string, groupItems: { [key: string]: any } }) {
      commit('setGroupTranslations', payload);
    },

    // Очистка переводов
    clearTranslations({ commit }) {
      commit('clearTranslations');
    },

    // Обновление заголовка страницы
    updatePageTitle({ commit, state }, payload: { pageName: string; title: string }) {
      const { pageName, title } = payload;
      
      // Проверяем, что текущая страница соответствует обновляемой
      if (state.translatePage?.name !== pageName) {
        console.warn(`updatePageTitle: pageName mismatch. Expected: ${state.translatePage?.name}, got: ${pageName}`);
        return;
      }
      
      // Проверяем наличие breadCrumbs
      if (!state.translatePage?.breadCrumbs) {
        console.warn(`updatePageTitle: breadCrumbs is null for page: ${pageName}`);
        return;
      }
      
      commit('updatePageTitle', { title });
    },

    // Обновление breadcrumb страницы
    updatePageBreadcrumb({ commit, state }, payload: { pageName: string; breadCrumb: string }) {
      const { pageName, breadCrumb } = payload;
      
      // Проходим по цепочке breadCrumbs и ищем нужную страницу
      let currentBreadCrumb = state.translatePage?.breadCrumbs;
      let found = false;
      
      while (currentBreadCrumb) {
        // Сравниваем pageName с URL (убираем слеш в начале)
        const breadCrumbPageName = currentBreadCrumb.url.replace(/^\//, '');
        
        if (breadCrumbPageName === pageName) {
          found = true;
          break;
        }
        
        currentBreadCrumb = currentBreadCrumb.parent;
      }
      
      if (!found) {
        console.warn(`updatePageBreadcrumb: pageName ${pageName} not found in breadCrumbs chain`);
        return;
      }
      
      commit('updatePageBreadcrumb', { pageName, breadCrumb });
    },

    // Обновление entity глобально
    updateEntityGlobal({ commit }, payload: { entityName: string; one: string; many: string }) {
      commit('updateEntityGlobal', payload);
    },

    // Обновление page item глобально
    updatePageItemGlobal({ commit, state }, payload: { pageName: string; pageItemCode: string; text: string }) {
      const { pageName, pageItemCode, text } = payload;
      
      // Проверяем, что pageName правильный
      if (state.translatePage?.name !== pageName) {
        console.warn(`updatePageItemGlobal: pageName mismatch. Expected: ${state.translatePage?.name}, got: ${pageName}`);
        return;
      }
      
      commit('updatePageItemGlobal', { pageName, pageItemCode, text });
    },

    // Обновление group item глобально
    updateGroupItemGlobal({ commit, state }, payload: { groupName: string; groupItemCode: string; text: string }) {
      const { groupName, groupItemCode, text } = payload;
      
      // Проверяем, что группа существует
      if (!state.translateGroups[groupName]) {
        console.warn(`updateGroupItemGlobal: group ${groupName} not found in translateGroups`);
        return;
      }
      
      // Проверяем, что элемент группы существует
      if (!(groupItemCode in state.translateGroups[groupName])) {
        console.warn(`updateGroupItemGlobal: groupItemCode ${groupItemCode} not found in group ${groupName}`);
        return;
      }
      
      commit('updateGroupItemGlobal', { groupName, groupItemCode, text });
    },
  },
};

export default i18nModule; 